<template>
  <div class="page-header">
    <!-- Back link (optional) -->
    <router-link
      v-if="backLinkTo"
      :to="backLinkTo"
      class="back-link"
    >
      <i class="fas fa-arrow-left" />
      <span>{{ backLinkText }}</span>
    </router-link>

    <div class="header-content">
      <div class="header-left">
        <div class="title-row">
          <h1 class="page-title">
            {{ title }}
          </h1>
          <!-- Slot for badge or element next to title -->
          <slot name="title-suffix" />
        </div>
        <p
          v-if="summary"
          class="page-summary"
        >
          {{ summary }}
        </p>
      </div>
      <div
        v-if="$slots.actions"
        class="header-actions"
      >
        <slot name="actions" />
      </div>
    </div>

    <!-- Slot for alert or content after header -->
    <slot name="after" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'PageHeader',

  props: {
    /**
     * Main page title
     */
    title: {
      type: String,
      required: true,
    },

    /**
     * Page description/summary (optional)
     */
    summary: {
      type: String,
      default: '',
    },

    /**
     * Back link route (optional) - Can be a string path or a route object
     */
    backLinkTo: {
      type: [String, Object],
      default: '',
    },

    /**
     * Back link text (optional)
     */
    backLinkText: {
      type: String,
      default: '',
    },
  },
});
</script>

<style scoped lang="scss">
.page-header {
  padding: 0 0 1rem 0;
  margin-bottom: 1rem;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  margin-bottom: 0.75rem;
  transition: color 0.2s ease;

  &:hover {
    color: var(--secondary-700);
  }

  i {
    font-size: 0.75rem;
  }
}

.title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.page-summary {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  margin: 0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
  line-height: 1.2;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
